import { lazy } from 'react'
import { isChunkLoadError } from '@/components/common/chunkLoadError'

/**
 * Recovers from stale chunks after a deploy (Sentry GRAPHIT-3R, GRAPHIT-4Y).
 *
 * A tab still running the previous `index-*.js` asks for hashed chunks the
 * deploy deleted. The only cure is a full reload onto the new build, so the
 * first failure for a chunk reloads the page. A `sessionStorage` flag per key
 * makes that happen at most once: if the chunk is still missing after the
 * reload, the error is rethrown and the error boundary explains it.
 */

export const CHUNK_RELOAD_FLAG_PREFIX = 'graphit:chunk-reload:'
const VITE_PRELOAD_ERROR_EVENT = 'vite:preloadError'

/** Storage can throw (private mode, blocked site data); treat that as "no flag". */
function readFlag(key) {
  try {
    return window.sessionStorage.getItem(CHUNK_RELOAD_FLAG_PREFIX + key)
  } catch {
    return null
  }
}

/** @returns {boolean} whether the flag was stored */
function writeFlag(key) {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG_PREFIX + key, String(Date.now()))
    return true
  } catch {
    return false
  }
}

function clearFlag(key) {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG_PREFIX + key)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/**
 * Reload the page unless this key already triggered a reload this session.
 * Without storage the guard cannot be recorded, so no reload happens: better
 * to show the error than to risk a reload loop.
 *
 * @param {string} key
 * @returns {boolean} whether a reload was started
 */
export function reloadOnceForKey(key) {
  if (readFlag(key) !== null) return false
  if (!writeFlag(key)) return false
  window.location.reload()
  return true
}

/**
 * Run a dynamic import, reloading once if its chunk is gone.
 *
 * @template T
 * @param {() => Promise<T>} importer - e.g. `() => import('@/pages/X')`
 * @param {string} key - stable per chunk, e.g. `'route:MetricDetailPage'`
 * @returns {Promise<T>} the module; stays pending while the page reloads so
 *   Suspense keeps its fallback instead of flashing the error boundary
 */
export async function importWithRetry(importer, key) {
  let mod
  try {
    mod = await importer()
  } catch (error) {
    if (isChunkLoadError(error) && reloadOnceForKey(key)) {
      return new Promise(() => {})
    }
    throw error
  }
  if (mod == null) return missingModule(key)
  clearFlag(key)
  return mod
}

/**
 * An import that resolved to nothing is a missing chunk Vite swallowed.
 *
 * In a build every `import()` goes through Vite's preload helper; when a
 * `vite:preloadError` listener cancels the event - installPreloadErrorReload
 * does, whenever it starts a reload - the helper returns instead of
 * rethrowing, so the import resolves to `undefined`. Handing that to
 * React.lazy crashed the page before the reload landed ("Cannot read
 * properties of undefined (reading 'default')", Sentry GRAPHIT-5G).
 *
 * @param {string} key
 * @returns {Promise<never>} pending while the page reloads
 */
function missingModule(key) {
  if (reloadOnceForKey(key)) return new Promise(() => {})
  // Already reloaded for this chunk: let the boundary say the app was updated.
  throw new TypeError(`Failed to fetch dynamically imported module: ${key} loaded no module`)
}

/**
 * `React.lazy` that survives a deploy. Use instead of `lazy()` everywhere.
 *
 * @param {() => Promise<{ default: React.ComponentType<any> }>} importer
 * @param {string} key - stable per chunk, e.g. `'route:MetricDetailPage'`
 */
export function lazyWithRetry(importer, key) {
  return lazy(() => importWithRetry(importer, key))
}

/**
 * Vite dispatches `vite:preloadError` when a chunk's preload (its CSS or JS
 * dependencies) fails. Reload once per failing asset; on a repeat, leave the
 * event alone so Vite throws and the boundary shows the error.
 *
 * @param {Window} [target=window]
 * @returns {() => void} removes the listener
 */
export function installPreloadErrorReload(target = window) {
  const handleError = (event) => {
    const detail = String(event?.payload?.message || 'unknown')
    if (reloadOnceForKey(`preload:${detail}`)) event.preventDefault()
  }
  target.addEventListener(VITE_PRELOAD_ERROR_EVENT, handleError)
  return () => target.removeEventListener(VITE_PRELOAD_ERROR_EVENT, handleError)
}
